import { IdentityDocumentType } from '@services/identityVerification/IdentityVerification.types';
import { DocumentDetail } from '@views/IdentityVerificationDocumentUpload/documentDetailReducer';

export type CustomerDocumentType = IdentityDocumentType | 'Uber Profile';

// For the single-document upload endpoint, which accepts document types beyond the identity-specific ones.
export class CustomerDocumentUploadRequest {
    constructor(
        public documentType: string,
        public documentNumber?: string,
        public expiryDate?: string,
        public fileNames?: string[],
        public additionalData?: Record<string, any>,
        public zohoModule?: string,
        public zohoModuleUniqueId?: string,
    ) { }

    static fromDocumentDetail(
        documentType: CustomerDocumentType,
        detail: DocumentDetail
    ): CustomerDocumentUploadRequest {
        const fileNames: string[] = [];

        // Add each of front & back files if they exist
        if (detail.frontFile) {
            fileNames.push(detail.frontFile.name);
        }
        if (detail.backFile) {
            fileNames.push(detail.backFile.name);
        }

        // Comprehensive additional data for most customer document types
        const additionalData = {
            // fields found on identity verification files for compatibility
            ...(detail.dateOfBirth && { DateOfBirth: detail.dateOfBirth }),
            ...(detail.state && { State: detail.state }),
            ...(detail.nationality && { Nationality: detail.nationality }),
            ...(detail.issueDate && { IssueDate: detail.issueDate }),
            ...(detail.streetLine1 && {
                AddressLines: [
                    detail.streetLine1,
                    ...(detail.streetLine2 ? [detail.streetLine2] : [])
                ]
            }),
            ...(detail.city && { Suburb: detail.city }),
            ...(detail.postCode && { PostCode: detail.postCode }),
            ...(detail.country && { Country: detail.country }),

            // Uber profile specific fields
            ...(detail.tripsCompleted && { trips_completed: detail.tripsCompleted }),
            ...(detail.uberTenure && { uber_tenure: detail.uberTenure }),
            ...(detail.drivingHoursPerWeek && { hours_driven_per_week: detail.drivingHoursPerWeek }),
            ...(detail.uberRating !== null && { uber_rating: detail.uberRating }),
        };

        return new CustomerDocumentUploadRequest(
            documentType, // the type passed in separately, which might be any customer-related document type
            detail.documentNumber || undefined,
            detail.expiryDate || undefined,
            fileNames.length > 0 ? fileNames : undefined,
            Object.keys(additionalData).length > 0 ? additionalData : undefined
        );
    }

    toJSON(): object {
        // Extract file extensions from each filename
        const fileExtensions = this.fileNames?.map(name => name.split('.').pop() || '');

        return {
            document_type: this.documentType,
            ...(this.documentNumber && { document_number: this.documentNumber }),
            ...(this.expiryDate && { expiry_date: this.expiryDate }),
            ...(this.fileNames && { files_original_name: this.fileNames }),
            ...(fileExtensions && { files_extension: fileExtensions }),
            ...(this.additionalData && { additional_data: this.additionalData }),
            ...(this.zohoModule && { zoho_module: this.zohoModule }),
            ...(this.zohoModuleUniqueId && { zoho_module_unique_id: this.zohoModuleUniqueId }),
        };
    }
}

export interface IPresignedUploadURL {
    file_name: string | null;
    description: string | null;
    presigned_upload_url: string | null;
}

export interface ICustomerDocumentUploadResponse {
    document_store_record_id: number;
    presigned_upload_urls: IPresignedUploadURL[];
}

export interface UploadParams {
    presignedUrl: string;
    file: File;
    contentType: string;
}

export interface UploadResult {
    success: boolean;
    response?: Response;
    error?: string;
}
